import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  SxProps,
  Theme,
} from '@mui/material';
import { ReactNode } from 'react';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  Validate,
} from 'react-hook-form';

export interface RhfCheckboxProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends Pick<ControllerProps<TFieldValues, TName>, 'control' | 'name'> {
  label: string;
  helperText?: ReactNode;
  disabled?: boolean;

  /**
   * Return an error message or `undefined` if the field has no errors.
   */
  validate?: Validate<boolean>;
  onChange?: (checked: boolean) => void;

  sx?: SxProps<Theme>;
}

export function RhfCheckbox<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  helperText,
  disabled = false,
  validate,
  onChange,
  sx,
}: RhfCheckboxProps<TFieldValues, TName>) {
  return (
    <Controller
      control={control}
      name={name}
      rules={{ validate }}
      render={({ field, fieldState }) => {
        return (
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    onChange?.(e.target.checked);
                  }}
                  disabled={disabled}
                  sx={sx}
                />
              }
              label={label}
            />
            {fieldState.error && (
              <FormHelperText error={!!fieldState.error}>
                {fieldState.error?.message ?? helperText}
              </FormHelperText>
            )}
          </div>
        );
      }}
    />
  );
}
